import CloseIcon from '@mui/icons-material/Close'
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material'
import parse from 'html-react-parser'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  getArticles,
  getArticlesById,
  markUsefulArticle,
} from '../../../redux/actions/KnowledgeBase/article'
import FileViewFromBlobUrl from '../../MobileComponents/MobileCommon/FileView'
import AppLoader from '../../common/AppLoader'
import PrintArticleWrapper from '../PrintArticleWrapper'
import Breadcrumbs from './../../common2/Breadcrumbs'

interface Article {
  id: number | string
  name: string
  body?: string
  isActive?: boolean
  articleAttachmentList?: { attachmentPath: string }[]
  [key: string]: any
}

interface ReviewPopupProps {
  open: boolean
  onSubmit: () => void
  onCancel: () => void
  showMessageOnSubmit: boolean
}

const ReviewPopup: React.FC<ReviewPopupProps> = ({ open, onSubmit, onCancel, showMessageOnSubmit }) => {
  const theme = useTheme()
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="review-dialog-title"
      aria-describedby="review-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="review-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography component="span">
            Was this article helpful ?
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onCancel}
            sx={{
              position: 'absolute',
              right: theme.spacing(1),
              top: theme.spacing(1),
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onCancel} color="secondary"
             startIcon={<span style={{ fontSize: '1.2rem' }}>👎</span>}
          >
            No
          </Button>
          <Button variant="contained" onClick={() => {
              if (showMessageOnSubmit) {
                onSubmit()
              } else {
                onCancel()
              }
            }}     
            startIcon={<span style={{ fontSize: '1.2rem' }}>👍</span>}
            color="primary"autoFocus>
            Yes
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

const ArticleThumb: React.FC<{ data: Article; onThumbClick: (article: Article) => void; isActive: boolean }> = ({
  data,
  onThumbClick,
  isActive,
}) => {
  return (
    <Grid
      onClick={() => onThumbClick(data)}
      container
      alignItems="center"
      className={`pb-1 cursor-pointer aid_list_items ${isActive ? "active" : ""}`}
      spacing={1}
      sx={{ cursor: 'pointer', marginTop:'0px !important' }}
    >
      <Grid item xs={3}>
        <Avatar
          alt="Thumb Image"
          src="https://images.unsplash.com/photo-1611162616475-46b635cb6868?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
          sx={{ height: 40, width: 40 }}
        />
      </Grid>
      <Grid item xs={9}>
        <Typography variant="body2">{data?.name || 'Article Name'}</Typography>
      </Grid>
    </Grid>
  )
}

const ViewArticleSSP: React.FC = () => {
  const path = useLocation()
  const searchPath = path.search
  const param = useParams<{ id?: string }>()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [articleVal, setArticleVal] = useState<Article | null>(null)
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([])
  const [list, setList] = useState<Article[]>([])
  const [showReviewPopup, setShowReviewPopup] = useState(false)
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)
  const [reviewedArticles, setReviewedArticles] = useState<(string | number)[]>([])
  const [loading, setLoading] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [hasGivenFeedback, setHasGivenFeedback] = useState(false)
  const [selectedId, setSelectedId] = useState<string | number | null>(null);

  const searchVal = new URLSearchParams(searchPath).get('searchVal') || ''

  useEffect(() => {
    dispatch(getArticles(getCallback, true))
  }, [])

  const getCallback = (resStatus: string, res: Article[]) => {
    if (resStatus === '0') {
      setList(res)
      const relative = res.filter((i) => {
        const objVals = `${Object.values(i)}`.toLowerCase()
        const { isActive, id } = i
        return isActive && param?.id !== `${id}` && objVals.indexOf(searchVal.toLowerCase()) >= 0
      })
      setRelatedArticles(relative)
    }
  }

  useEffect(() => {
    if (param?.id) {
      setLoading(true);
            dispatch(getArticlesById(param.id, getArticlesByIdCB, true))
    }
  }, [param?.id])

  const getArticlesByIdCB = (resStatus: string, res: Article) => {
    setLoading(false);
    if (resStatus === '0') {
      setArticleVal(res)
      setHasGivenFeedback(false)
    }
  }

  const markArticleUseful = () => {
    if (articleVal?.id) {
      setReviewedArticles((prev) => [...prev, articleVal.id])
      dispatch(markUsefulArticle(articleVal.id))
      setFeedbackMessage('Thanks for your feedback!')
      setFeedbackSubmitted(true)
      setHasGivenFeedback(true)
    }
  }

  const handleNegativeFeedback = () => {
    if (articleVal?.id) {
      setReviewedArticles((prev) => [...prev, articleVal.id])
      setFeedbackMessage('Thanks for your feedback!')
      setFeedbackSubmitted(true)
      setHasGivenFeedback(true)
    }
  }

  const handleArticleThumbClick = (article: Article) => {
    setSelectedId(article.id);
    if (!hasGivenFeedback && !reviewedArticles.includes(article.id)) {
      setSelectedArticle(article)
      setShowReviewPopup(true)
    } else {
      navigate(`/ArticlesList/${article.id}`)
    }
  }

  const handleReviewSubmit = () => {
    if (selectedArticle) {
      setReviewedArticles((prev) => [...prev, selectedArticle.id])
      dispatch(markUsefulArticle(selectedArticle.id))
      setShowReviewPopup(false)
      setFeedbackMessage('We appreciate your honesty. We\'ll use this feedback to improve our articles.')
      setFeedbackSubmitted(true)
      setHasGivenFeedback(true)
      navigate(`/ArticlesList/${selectedArticle.id}`)
    }
  }

  const handleReviewCancel = () => {
    if (selectedArticle) {
      setShowReviewPopup(false)
      navigate(`/ArticlesList/${selectedArticle.id}`)
    }
  }

  const handleCloseSnackbar = () => {
    setFeedbackSubmitted(false)
  }

  return (
    <Box className="p-sm-0 p-2">
      <Breadcrumbs
        data={[
          {
            title: 'Knowledge Base ',
            path: '/',
          },
          {
            title: 'Article List ',
            path: '/ArticlesSSP',
          },
          {
            title: 'View Article ',
            path: `/ArticlesList/${param?.id || 0}?searchVal=${searchVal}`,
          },
        ]}
      />
      <AppLoader enabled={loading} />
      <Grid container spacing={1} className="pt-1">
        <Grid item md={8}>
          <PrintArticleWrapper title={`# Article ID : ${articleVal?.id ?? ''}`} name="Article">
            <Box className="white-bg article-viewer-box p-2">
              {articleVal && (
                <>
                  <Typography variant="h4">{articleVal.name}</Typography>
                  <hr />
                  {parse(articleVal.body || '')}
                </>
              )}
            </Box>

            {articleVal?.articleAttachmentList && articleVal.articleAttachmentList.length > 0 && (
              <>
                <hr />
                <Box className="white-bg p-2">
                  <Typography variant="h6" gutterBottom>
                    Attachments
                  </Typography>
                  <Box className="row">
                    {articleVal.articleAttachmentList.map((attachment, index) => (
                      <Box className="col-auto p-3p" key={index}>
                        <FileViewFromBlobUrl url={attachment.attachmentPath} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {articleVal?.id && !reviewedArticles.includes(articleVal.id) && !hasGivenFeedback && (
              <Grid
                className="p-1 mt-1 white-bg print-article"
                container
                spacing={0}
                alignItems="center"
              >
                <Grid item md={7}>
                  <Typography>Was this article helpful ?</Typography>
                </Grid>
                <Grid item md={5} className="text-right print-article-btn">
                  <Button
                    onClick={handleNegativeFeedback}
                    color="secondary"
                    variant="outlined"
                    startIcon={<span style={{ fontSize: '1.2rem' }}>👎</span>}
                  >
                    No
                  </Button>
                  <Button
                    onClick={markArticleUseful}
                    sx={{ ml: 1 }}
                    variant="contained" color="primary"
                    startIcon={<span style={{ fontSize: '1.2rem' }}>👍</span>}
                  >
                    Yes
                  </Button>
                </Grid>
              </Grid>
            )}
          </PrintArticleWrapper>
        </Grid>
        <Grid item md={4} xs={12} className="ssp-arcticle">
          <aside className="aside">
            <Box className="white-bg p-2">
              <Typography variant="h6">Related Articles</Typography>
              <hr />
              {relatedArticles.slice(0, 5).map((article) => (
                <ArticleThumb key={article.id} data={article} onThumbClick={handleArticleThumbClick} isActive={selectedId === article.id} />
              ))}
            </Box>

            <Box className="pt-1" />

            <Box className="white-bg p-2">
              <Typography variant="h6">Trending Articles</Typography>
              <hr />
              {list.slice(0, 5).map((article) => (
                <ArticleThumb key={article.id} data={article} onThumbClick={handleArticleThumbClick} isActive={selectedId === article.id} />
              ))}
            </Box>
          </aside>
        </Grid>
      </Grid>

      {selectedArticle && !hasGivenFeedback && !reviewedArticles.includes(selectedArticle.id) && (
        <ReviewPopup 
          open={showReviewPopup} 
          onSubmit={handleReviewSubmit} 
          onCancel={handleReviewCancel}
          showMessageOnSubmit={true}
        />
      )}

      <Snackbar
        open={feedbackSubmitted}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {feedbackMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ViewArticleSSP